import React from 'react';
import PropTypes from 'prop-types';

import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Row,
  Spinner,
  Table,
  Tooltip,
  OverlayTrigger,
} from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';

import ActionDropDown from 'components/shared/ActionDropDown';
import SimilarityThresholdConfig from 'components/sku_mapping_workflow/SimilarityThresholdConfig';
import { areYouSure, SwalToast } from 'lib/utils/toast';
import { blue, blue1, fontGrey, green, green2 } from 'lib/css/colors';
import { ProductActiveStatus } from 'lib/enums/ProductActiveStatus';
import {
  SKU_MAPPING_WORKFLOW,
  SKU_MAPPING_WORKFLOW_RETRY,
  PRESIGNED_URL,
  FILE_EXPORTS_PRODUCT_DATA_WITH_GMV_AND_MAPPING,
} from 'lib/networking/endpoints';
import {
  SkuMappingWorkflowStepStatusConfig,
  SkuMappingWorkflowTypes,
} from 'lib/enums/SkuMappingWorkflowEnum';
import { titleCase } from 'lib/utils/StringProcess';
import UserPermission from 'lib/enums/UserPermission';
import useUserPermissions from 'lib/hooks/useUserPermissions';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

const SkuMappingWorkflow = ({ workflowType, workflowValue }) => {
  const hasUserPermissions = useUserPermissions();
  const {
    data: { data: statusData = [] } = {},
    loading: statusLoading,
    refetch: refetchStatus,
  } = useGet(
    SKU_MAPPING_WORKFLOW,
    {
      workflow_type: workflowType,
      workflow_value: workflowValue,
    },
    !workflowValue,
  );

  // Start workflow
  const { postData: startWorkflow, loading: startingWorkflow } = usePost(
    SKU_MAPPING_WORKFLOW,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'SKU mapping workflow started successfully',
      });
      refetchStatus();
    },
    error =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Failed to start workflow: ${error?.response?.data?.message || 'Unknown error'}`,
      }),
  );

  // Retry workflow
  const { postData: retryWorkflow, loading: retryingWorkflow } = usePost(
    SKU_MAPPING_WORKFLOW_RETRY,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'SKU mapping workflow retried successfully',
      });
      refetchStatus();
    },
    error =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Failed to retry workflow: ${error?.response?.data?.message || 'Unknown error'}`,
      }),
  );

  // Get presigned URL for file downloads
  const { postData: getPresignedUrl } = usePost(
    PRESIGNED_URL,
    response => {
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = response.data.presigned_url;
      link.download = response.data.file_url.split('/').pop() || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      SwalToast('success', 'File download started');
    },
    () => {
      SwalToast('error', 'Failed to download file');
    },
  );

  const { postData: exportProductDataWithGmvAndMapping } = usePost(
    FILE_EXPORTS_PRODUCT_DATA_WITH_GMV_AND_MAPPING,
    () => {
      SwalToast('success', 'Started Successfully, please check the file exports page');
    },
  );

  const getStatusBadge = status => {
    const config = SkuMappingWorkflowStepStatusConfig[status] || {
      variant: 'secondary',
      text: status,
    };
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  const handleStartWorkflow = productStatus => {
    Swal.fire({
      title: 'Start SKU Mapping Workflow?',
      text: 'This will start all the SKU mapping steps for this vendor catalog.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Start Workflow',
      cancelButtonText: 'Cancel',
    }).then(result => {
      if (result.isConfirmed) {
        startWorkflow({
          workflow_type: workflowType,
          workflow_value: workflowValue,
          product_status: productStatus || null,
        });
      }
    });
  };

  const formatProcessingTime = seconds => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Function to check if a value is a file URL
  const isFileUrl = value => {
    if (typeof value !== 'string') return false;

    return value.includes('s3');
  };

  // Function to handle file download
  const handleFileDownload = fileUrl => {
    getPresignedUrl({ file_url: fileUrl });
  };

  // Function to copy file URL to clipboard
  const handleCopyToClipboard = async fileUrl => {
    try {
      await navigator.clipboard.writeText(fileUrl);
      SwalToast('success', 'File URL copied to clipboard');
    } catch (error) {
      SwalToast('error', 'Failed to copy URL to clipboard');
    }
  };

  const renderStatisticsMetadata = metadata => {
    if (!metadata || Object.keys(metadata).length === 0) {
      return <span className="text-muted">No statistics available</span>;
    }

    return (
      <div className={css(styles.statisticsContainer)}>
        {Object.entries(metadata).map(([key, value]) => {
          const isFile = isFileUrl(value);
          const isErrorKey = key.toLowerCase() === 'error';

          return (
            <div key={key} className={css(styles.statisticItem)}>
              <div
                className={css(styles.statisticDescription, isFile && styles.clickableDescription)}
                onClick={isFile ? () => handleCopyToClipboard(value) : undefined}
                title={isFile ? 'Click to copy file URL to clipboard' : undefined}
              >
                {titleCase(key.replace(/_/g, ' '))}
              </div>
              <div className={css(styles.statisticValue)}>
                {isFile ? (
                  <div className={css(styles.fileDownloadContainer)}>
                    <span className={css(styles.fileName)}>{value.split('/').pop() || 'File'}</span>
                    <FontAwesomeIcon
                      icon={faDownload}
                      className={css(styles.downloadIcon)}
                      onClick={() => handleFileDownload(value)}
                      title="Download file"
                    />
                  </div>
                ) : isErrorKey ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-${key}`}>{value}</Tooltip>}
                  >
                    <span style={{ cursor: 'pointer', textDecoration: 'underline' }}>
                      Error (hover for details)
                    </span>
                  </OverlayTrigger>
                ) : (
                  value
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (statusLoading) {
    return (
      <div className="d-flex justify-content-center p-4">
        <Spinner animation="border" role="status">
          <span className="sr-only">Loading workflow status...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">SKU Mapping Workflow</h5>
            <div className="d-flex align-items-center">
              {workflowType === SkuMappingWorkflowTypes.VENDOR &&
                hasUserPermissions([UserPermission.EXPORT_PRODUCT_DATA]) && (
                  <ActionDropDown
                    submenuItems={[
                      {
                        title: 'Products along with SKU Mapping and GMV',
                        action: () =>
                          exportProductDataWithGmvAndMapping({
                            global_vendor_id: workflowValue,
                          }),
                      },
                    ]}
                    label="Reports"
                    className="mr-2"
                  />
                )}
              <SimilarityThresholdConfig />
              <ActionDropDown
                submenuItems={[
                  {
                    title: 'Process the active products',
                    action: () => handleStartWorkflow(ProductActiveStatus.Active),
                  },
                  {
                    title: 'Process the entire catalog',
                    action: () => handleStartWorkflow(),
                  },
                ]}
                label={startingWorkflow ? 'Starting...' : 'Start Workflow'}
                className="mr-2"
              />
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => refetchStatus()}
                disabled={statusLoading}
                className="ml-2"
              >
                Refresh
              </Button>
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          {statusData.length === 0 ? (
            <Alert variant="info">
              Workflow not initialized for this catalog. Click Start Workflow to begin.
            </Alert>
          ) : (
            <div>
              <Row className="mb-3">
                <Col>
                  <h6>Workflow Steps</h6>
                </Col>
              </Row>

              <Table responsive striped>
                <thead>
                  <tr>
                    <th>Step</th>
                    <th>Status</th>
                    <th>Started At</th>
                    <th>Finished At</th>
                    <th>Processing Time</th>
                    <th>Statistics</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {statusData.map(step => (
                    <tr key={step.id}>
                      <td>
                        <div>
                          <strong>{step.step}</strong>
                        </div>
                      </td>
                      <td>{getStatusBadge(step.status)}</td>
                      <td>
                        {step.started_at ? (
                          new Date(step.started_at).toLocaleString()
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {step.completed_at ? (
                          new Date(step.completed_at).toLocaleString()
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>{formatProcessingTime(step.processing_time_seconds)}</td>
                      <td>{renderStatisticsMetadata(step.statistics_metadata)}</td>
                      <td>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() =>
                            areYouSure(
                              () =>
                                retryWorkflow({
                                  step: step.step,
                                  workflow_type: workflowType,
                                  workflow_value: workflowValue,
                                  product_status: step.product_status,
                                }),
                              'Are you sure you want to retry this step? This will delete all the steps after this step',
                            )
                          }
                        >
                          Retry
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

const styles = StyleSheet.create({
  statisticsContainer: {
    fontSize: '12px',
    padding: '4px',
  },
  statisticDescription: {
    color: fontGrey,
    marginBottom: '4px',
    fontWeight: '500',
  },
  clickableDescription: {
    cursor: 'pointer',
    color: blue,
    textDecoration: 'underline',
    ':hover': {
      color: blue1,
      textDecoration: 'underline',
    },
  },
  statisticItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '2px',
  },
  statisticValue: {
    fontWeight: 'bold',
    color: fontGrey,
  },
  fileDownloadContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  fileName: {
    fontSize: '11px',
    color: blue,
    maxWidth: '120px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  downloadIcon: {
    cursor: 'pointer',
    color: green,
    fontSize: '14px',
    ':hover': {
      color: green2,
    },
  },
});

SkuMappingWorkflow.propTypes = {
  workflowType: PropTypes.string.isRequired,
  workflowValue: PropTypes.string.isRequired,
};

export default SkuMappingWorkflow;
